import { useEffect, useState } from 'react';
import PageTitle from '../../components/PageTitle';
import SafeImage from '../../components/SafeImage';
import { clientService } from '../../services/clientService';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const MIN_MARQUEE_SLIDES = 12;

const buildMarqueeItems = (items) => {
  if (items.length === 0 || items.length >= MIN_MARQUEE_SLIDES) return items;
  const repeated = [];
  while (repeated.length < MIN_MARQUEE_SLIDES) {
    repeated.push(...items);
  }
  return repeated;
};

const Clients = () => {
  useDocumentTitle("Our Clients");
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const marqueeClients = buildMarqueeItems(clients);

  useEffect(() => {
    fetchAsync();
  }, []);

  useEffect(() => {
    if (loading || error || clients.length === 0) return;

    const timer = setTimeout(() => {
      const Swiper = window.Swiper;
      if (!Swiper) return;
      const sliderEl = document.querySelector(".our-clients-slider");
      if (!sliderEl) return;
      if (sliderEl.swiper) sliderEl.swiper.destroy(true, true);

      const swiper = new Swiper(".our-clients-slider", {
        slidesPerView: 2,
        spaceBetween: 30,
        loop: true,
        loopedSlides: marqueeClients.length,
        loopAdditionalSlides: 10,
        freeMode: true,
        speed: 5000,
        allowTouchMove: false,
        simulateTouch: false,
        preventClicks: false,
        preventClicksPropagation: false,
        autoplay: { delay: 0, disableOnInteraction: false },
        breakpoints: {
          480: { slidesPerView: 2 },
          768: { slidesPerView: 3 },
          1024: { slidesPerView: 4 },
          1200: { slidesPerView: 5 },
        },
      });

      const stopAutoplay = () => swiper.autoplay?.stop();
      const startAutoplay = () => swiper.autoplay?.start();
      sliderEl.addEventListener("mouseenter", stopAutoplay);
      sliderEl.addEventListener("mouseleave", startAutoplay);
    }, 300);

    return () => clearTimeout(timer);
  }, [loading, error, clients]);

  const fetchAsync = async () => {
    try {
      setError("");
      setLoading(true);
      const response = await clientService.getPublishedAsync();
      if (!response.isSuccess) {
        setError(response.errors?.[0] || "Unable to load clients right now. Please try again later.");
        setClients([]);
        return;
      }
      setClients(response.value || []);
    } catch (err) {
      setError("Unable to load clients right now. Please try again later.");
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageTitle title="Our Clients" />
      <section className="brand-section our-clients-section pt-120 pb-120">
        <div className="container">
          {loading && (
            <div className="row">
              <div className="col-12 text-center">
                <p className="text-white mb-0">Loading clients...</p>
              </div>
            </div>
          )}

          {!loading && error && (
            <div className="row">
              <div className="col-12 text-center">
                <p className="text-white mb-0">{error}</p>
              </div>
            </div>
          )}

          {!loading && !error && clients.length === 0 && (
            <div className="row">
              <div className="col-12 text-center">
                <p className="text-white mb-0">No clients to display yet.</p>
              </div>
            </div>
          )}

          {!loading && !error && clients.length > 0 && (
            <div className="outer-box">
              <div className="swiper our-clients-slider">
                <div className="swiper-wrapper">
                  {marqueeClients.map((client, index) => (
                    <div className="swiper-slide" key={`${client.id}-${index}`} style={{ width: 224 }}>
                      <a href={`/clients/${client.slug}`} className="client-logo-card" title={client.name}>
                        <SafeImage src={client.logoPath} alt={client.name} />
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Clients;
